import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import {
  ArrowUpRight,
  Bed,
  Snowflake,
  Library,
  UtensilsCrossed,
  Laptop,
  Briefcase,
  GraduationCap,
  MapPin,
  Mail,
  MessageCircle,
  BookOpen,
} from "lucide-react";

const whatsappLink = "https://wa.me/917319188649";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { label: "Home", href: "/#top" },
  { label: "Facilities", href: "/#facilities" },
  { label: "Courses", href: "/#courses" },
  { label: "Admissions", href: "/#admissions" },
  { label: "Contact", href: "/#contact" },
];

const Marquee = () => {
  const items = [
    "REACT.JS",
    "PYTHON",
    "AWS",
    "DATA STRUCTURES",
    "INTERNSHIP GUIDANCE",
    "SYSTEM DESIGN",
    "FREE SKILL COURSES",
    "GIT & GITHUB",
    "CLOUD PROJECTS",
  ];

  return (
    <div className="marquee" data-testid="tech-marquee">
      <div className="marquee-track" data-testid="tech-marquee-track-primary">
        {items.map((item, index) => (
          <span key={`primary-${item}`} data-testid={`tech-marquee-item-${index}`}>
            {item}
          </span>
        ))}
      </div>
      <div className="marquee-track" aria-hidden data-testid="tech-marquee-track-secondary">
        {items.map((item, index) => (
          <span key={`secondary-${item}`} data-testid={`tech-marquee-item-dup-${index}`}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

const AuthCallback = ({ onComplete, onError }) => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.hash.replace("#", ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      onError?.("Missing session information.");
      return;
    }

    const exchangeSession = async () => {
      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "X-Session-ID": sessionId },
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Authentication failed");
        }
        const userData = await response.json();
        window.history.replaceState({}, document.title, window.location.pathname);
        onComplete?.(userData);
      } catch (error) {
        window.history.replaceState({}, document.title, window.location.pathname);
        onError?.("Unable to sign in. Please try again.");
      }
    };

    exchangeSession();
  }, [onComplete, onError]);

  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-zinc-300" data-testid="auth-callback-loading">
      Signing you in with Google...
    </div>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#09090b] text-white font-body relative">
      <div
        className="pointer-events-none fixed inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(circle at top, rgba(253,224,71,0.16), transparent 45%), radial-gradient(circle at 20% 20%, rgba(255,255,255,0.08), transparent 35%)",
        }}
        data-testid="background-glow"
      />
      <div className="pointer-events-none fixed inset-0 grain-overlay" data-testid="background-grain" />
      <header className="glass-header sticky top-0 z-50" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between h-20">
          <a href="/#top" className="flex items-center gap-3" data-testid="brand-link">
            <div
              className="h-10 w-10 border border-yellow-400/60 flex items-center justify-center text-yellow-300 font-bold font-mono"
              data-testid="brand-mark"
            >
              CS
            </div>
            <div className="leading-none" data-testid="brand-text">
              <div className="font-heading text-lg tracking-tight">CODESHED</div>
              <div className="text-xs text-zinc-400 font-mono">PG + Career Hub</div>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-6" data-testid="main-nav">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm uppercase tracking-[0.2em] font-mono transition-colors text-zinc-400 hover:text-white"
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3" data-testid="header-cta-group">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-200 text-sm font-semibold hover:border-yellow-400 hover:text-yellow-300 transition"
              data-testid="header-whatsapp-cta"
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 text-black font-bold text-sm rounded-sm hover:bg-yellow-300 transition"
              data-testid="header-contact-cta"
            >
              Contact
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </header>
      <div className="md:hidden border-b border-zinc-800 bg-zinc-950/80" data-testid="mobile-nav">
        <div
          className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4 overflow-x-auto"
          data-testid="mobile-nav-links"
        >
          {navItems.map((item) => (
            <a
              key={`mobile-${item.href}`}
              href={item.href}
              className="text-xs uppercase tracking-[0.2em] font-mono whitespace-nowrap text-zinc-400"
              data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>
      <main className="relative z-10" data-testid="main-content">
        {children}
      </main>
      <footer className="border-t border-zinc-800 py-12" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid gap-6 md:grid-cols-3">
          <div data-testid="footer-brand-block">
            <div className="font-heading text-xl">CODESHED</div>
            <p className="text-sm text-zinc-400 mt-2" data-testid="footer-tagline">
              Premium PG living and career guidance for CSE-focused students in Barasat.
            </p>
          </div>
          <div data-testid="footer-contact-block">
            <div className="text-sm uppercase tracking-[0.3em] font-mono text-zinc-500">Contact</div>
            <p className="mt-2 text-sm text-zinc-300" data-testid="footer-email">
              saycodeshed@gmail.com
            </p>
            <p className="text-sm text-zinc-300" data-testid="footer-whatsapp">
              WhatsApp: 7319188649
            </p>
          </div>
          <div data-testid="footer-location-block">
            <div className="text-sm uppercase tracking-[0.3em] font-mono text-zinc-500">Location</div>
            <p className="mt-2 text-sm text-zinc-300" data-testid="footer-location">
              Barasat, North 24 Parganas
            </p>
          </div>
        </div>
      </footer>
      <a
        href={whatsappLink}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:scale-110 transition-transform"
        data-testid="floating-whatsapp-cta"
      >
        <MessageCircle size={24} />
      </a>
    </div>
  );
};

const Home = () => {
  const highlights = [
    {
      title: "Comfort-first PG",
      description: "AC + Non-AC rooms, quality food, and a calm study-ready vibe.",
    },
    {
      title: "CSE-focused community",
      description: "Live with students learning the same tech stacks and goals.",
    },
    {
      title: "Career lift-off",
      description: "Free courses + internship and job guidance built in.",
    },
  ];

  const pathways = [
    {
      title: "Facilities",
      description: "See the rooms, library, food, and study spaces.",
      link: "#facilities",
    },
    {
      title: "Courses",
      description: "Explore free upskilling tracks with mentors.",
      link: "#courses",
    },
    {
      title: "Admissions",
      description: "Guidance after Class 12 and career roadmap.",
      link: "#admissions",
    },
  ];

  return (
    <div id="top" className="scroll-anchor" data-testid="home-page">
      <section className="relative overflow-hidden" data-testid="home-hero-section">
        <div className="absolute inset-0" data-testid="home-hero-image-wrapper">
          <img
            src="https://images.pexels.com/photos/4812885/pexels-photo-4812885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Tech workspace"
            className="h-full w-full object-cover"
            data-testid="home-hero-image"
          />
          <div className="absolute inset-0 bg-black/70" data-testid="home-hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 py-28 md:py-36">
          <div className="max-w-2xl" data-testid="home-hero-content">
            <div
              className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-400"
              data-testid="home-hero-overline"
            >
              Eat. Sleep. Code.
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mt-4"
              data-testid="home-hero-title"
            >
              CODESHED — the co-living tech hub for Class 12 achievers.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-zinc-300" data-testid="home-hero-subtitle">
              Comfortable PG living in Barasat with a library, dedicated study spaces, and free skill courses so you can focus on CSE careers.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8" data-testid="home-hero-cta-group">
              <a
                href="#contact"
                className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-sm inline-flex items-center gap-2 hover:bg-yellow-300 transition"
                data-testid="home-hero-book-cta"
              >
                Book a Room
                <ArrowUpRight size={18} />
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="px-6 py-3 border border-yellow-400/40 text-yellow-200 font-semibold rounded-sm inline-flex items-center gap-2 hover:border-yellow-300 hover:text-yellow-100 transition"
                data-testid="home-hero-whatsapp-cta"
              >
                Chat on WhatsApp
                <MessageCircle size={18} />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6" data-testid="home-hero-stats">
              <div data-testid="home-stat-rooms">
                <div className="text-sm text-zinc-400 uppercase font-mono">Rooms</div>
                <div className="text-xl font-heading">AC + Non-AC</div>
              </div>
              <div data-testid="home-stat-library">
                <div className="text-sm text-zinc-400 uppercase font-mono">Library</div>
                <div className="text-xl font-heading">Quiet + Focused</div>
              </div>
              <div data-testid="home-stat-guidance">
                <div className="text-sm text-zinc-400 uppercase font-mono">Guidance</div>
                <div className="text-xl font-heading">Career Ready</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="home-highlights-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-6" data-testid="home-highlights-header">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500" data-testid="home-highlights-overline">
                Why CODESHED
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl mt-3" data-testid="home-highlights-title">
                A premium PG built around your CSE journey.
              </h2>
            </div>
            <a
              href="#facilities"
              className="text-sm uppercase font-mono tracking-[0.2em] text-yellow-300 inline-flex items-center gap-2"
              data-testid="home-highlights-link"
            >
              Explore Facilities
              <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="grid gap-6 md:grid-cols-3 mt-10" data-testid="home-highlights-grid">
            {highlights.map((item, index) => (
              <div
                key={item.title}
                className="bg-zinc-900 border border-zinc-800 p-6 hover:-translate-y-1 hover:shadow-2xl hover:shadow-yellow-400/10 hover:border-yellow-400/50 transition-all"
                data-testid={`home-highlight-card-${index}`}
              >
                <h3 className="font-heading text-xl" data-testid={`home-highlight-title-${index}`}>
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-300" data-testid={`home-highlight-description-${index}`}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      <section className="py-20" data-testid="home-pathways-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div data-testid="home-pathways-header">
            <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500">The Pathways</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl mt-3" data-testid="home-pathways-title">
              Choose your next move after Class 12.
            </h2>
            <p className="text-sm text-zinc-300 mt-3" data-testid="home-pathways-description">
              From living comfort to career guidance, each page explains how CODESHED supports you.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3 mt-10" data-testid="home-pathways-grid">
            {pathways.map((item, index) => (
              <a
                href={item.link}
                key={item.title}
                className="bg-zinc-900 border border-zinc-800 p-6 hover:-translate-y-1 hover:border-yellow-400/50 transition-all"
                data-testid={`home-pathway-card-${index}`}
              >
                <div className="flex items-center justify-between" data-testid={`home-pathway-card-header-${index}`}>
                  <h3 className="font-heading text-xl" data-testid={`home-pathway-title-${index}`}>
                    {item.title}
                  </h3>
                  <ArrowUpRight size={18} className="text-yellow-300" />
                </div>
                <p className="mt-4 text-sm text-zinc-300" data-testid={`home-pathway-description-${index}`}>
                  {item.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <FacilitiesPage />
      <ImageTopicsSection />
      <CoursesPage />
      <AdmissionsPage />
      <ContactPage />
    </div>
  );
};

const FacilitiesPage = () => {
  return (
    <div id="facilities" className="py-16 scroll-anchor" data-testid="facilities-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div data-testid="facilities-header">
          <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500">The Shed</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-3" data-testid="facilities-title">
            Everything you need to live, study, and build a tech career.
          </h1>
          <p className="text-sm text-zinc-300 mt-4" data-testid="facilities-subtitle">
            Spacious rooms, a silent library, and a community that keeps you focused.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-12" data-testid="facilities-bento-grid">
          <div
            className="md:col-span-7 bg-zinc-900 border border-zinc-800 p-6 flex flex-col gap-4"
            data-testid="facility-card-study-space"
          >
            <img
              src="https://images.unsplash.com/photo-1632835256083-f044d6a859ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxzdHVkZW50cyUyMGNvZGluZyUyMHRvZ2V0aGVyJTIwbW9kZXJufGVufDB8fHx8MTc3NzAxNDA1Mnww&ixlib=rb-4.1.0&q=85"
              alt="Study space"
              className="w-full h-56 object-cover"
              data-testid="facility-study-image"
            />
            <div>
              <h3 className="font-heading text-2xl" data-testid="facility-study-title">
                Dedicated Tech Study Spaces
              </h3>
              <p className="text-sm text-zinc-300 mt-3" data-testid="facility-study-description">
                Large desks, ambient lighting, and plug points so you can stay in flow.
              </p>
            </div>
          </div>
          <div
            className="md:col-span-5 bg-zinc-900 border border-zinc-800 p-6 flex flex-col justify-between"
            data-testid="facility-library-card"
          >
            <Library className="text-yellow-300" size={28} />
            <div>
              <h3 className="font-heading text-2xl" data-testid="facility-library-title">
                High-Quiet Library
              </h3>
              <p className="text-sm text-zinc-300 mt-3" data-testid="facility-library-description">
                A calm zone for reading, prep, and uninterrupted focus.
              </p>
            </div>
          </div>
          <div className="md:col-span-4 bg-zinc-900 border border-zinc-800 p-6" data-testid="facility-food-card">
            <UtensilsCrossed className="text-yellow-300" size={28} />
            <h3 className="font-heading text-xl mt-4" data-testid="facility-food-title">
              Quality Food & Comfort
            </h3>
            <p className="text-sm text-zinc-300 mt-3" data-testid="facility-food-description">
              Healthy, consistent meals that keep your energy up for study sprints.
            </p>
          </div>
          <div className="md:col-span-8 bg-zinc-900 border border-zinc-800 p-6" data-testid="facility-rooms-card">
            <div className="flex items-center gap-3" data-testid="facility-rooms-header">
              <Bed className="text-yellow-300" size={28} />
              <h3 className="font-heading text-xl" data-testid="facility-rooms-title">
                AC + Non-AC Rooms
              </h3>
            </div>
            <p className="text-sm text-zinc-300 mt-4" data-testid="facility-rooms-description">
              Choose the comfort level that suits your routine. Clean, airy, and study-friendly rooms.
            </p>
            <div className="mt-6 flex flex-wrap gap-4" data-testid="facility-rooms-tags">
              <span className="border border-zinc-700 px-3 py-1 text-xs font-mono" data-testid="facility-rooms-tag-ac">
                AC OPTION
              </span>
              <span className="border border-zinc-700 px-3 py-1 text-xs font-mono" data-testid="facility-rooms-tag-nonac">
                NON-AC OPTION
              </span>
              <span className="border border-zinc-700 px-3 py-1 text-xs font-mono" data-testid="facility-rooms-tag-visit">
                Visit to know availability
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 mt-12" data-testid="facility-extras-section">
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="facility-ac-card">
            <div className="flex items-center gap-3" data-testid="facility-ac-header">
              <Snowflake className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="facility-ac-title">
                AC Rooms
              </h3>
            </div>
            <p className="text-sm text-zinc-300 mt-3" data-testid="facility-ac-description">
              Cool, quiet, and optimized for uninterrupted study even in summer.
            </p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="facility-nonac-card">
            <div className="flex items-center gap-3" data-testid="facility-nonac-header">
              <Bed className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="facility-nonac-title">
                Non-AC Rooms
              </h3>
            </div>
            <p className="text-sm text-zinc-300 mt-3" data-testid="facility-nonac-description">
              Airy rooms with excellent ventilation and calm ambience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageTopicsSection = () => {
  const topics = [
    {
      title: "Collaborative Coding",
      description: "Peer learning sessions inspired by real study rooms and coding circles.",
      image:
        "https://images.unsplash.com/photo-1632835256083-f044d6a859ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxzdHVkZW50cyUyMGNvZGluZyUyMHRvZ2V0aGVyJTIwbW9kZXJufGVufDB8fHx8MTc3NzAxNDA1Mnww&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Modern PG Living",
      description: "Comfortable rooms designed for rest and fast recovery between study sprints.",
      image:
        "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb21mb3J0YWJsZSUyMGhvc3RlbCUyMHJvb218ZW58MHx8fHwxNzc3MDE0MDUyfDA&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Mentored Sessions",
      description: "Guided planning and internship support with focused mentor check-ins.",
      image: "https://images.pexels.com/photos/7988744/pexels-photo-7988744.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      title: "Tech Focused Atmosphere",
      description: "A hacker-house vibe that keeps you accountable and career-ready.",
      image: "https://images.pexels.com/photos/4812885/pexels-photo-4812885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
  ];

  return (
    <section className="py-20" data-testid="image-topics-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div data-testid="image-topics-header">
          <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500">Inspired By The Space</div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl mt-3" data-testid="image-topics-title">
            Topics that match the real CODESHED environment.
          </h2>
          <p className="text-sm text-zinc-300 mt-3" data-testid="image-topics-description">
            Every visual space maps to the skills and habits students build inside the PG.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 mt-10" data-testid="image-topics-grid">
          {topics.map((topic, index) => (
            <div
              key={topic.title}
              className="bg-zinc-900 border border-zinc-800 overflow-hidden hover:-translate-y-1 hover:border-yellow-400/50 transition"
              data-testid={`image-topic-card-${index}`}
            >
              <img
                src={topic.image}
                alt={topic.title}
                className="w-full h-48 object-cover"
                data-testid={`image-topic-image-${index}`}
              />
              <div className="p-6">
                <h3 className="font-heading text-xl" data-testid={`image-topic-title-${index}`}>
                  {topic.title}
                </h3>
                <p className="text-sm text-zinc-300 mt-3" data-testid={`image-topic-description-${index}`}>
                  {topic.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CoursesPage = () => {
  const courses = [
    "Python for CSE",
    "Data Structures & Algorithms",
    "React + Frontend Foundations",
    "Database Basics (SQL)",
    "Operating Systems & Networks",
    "Git & GitHub",
    "Cloud Fundamentals (AWS)",
    "Interview Prep & Portfolio",
  ];

  const tracks = [
    {
      title: "Full-Stack Builder",
      description: "HTML, CSS, JavaScript, React, Node basics, and deployment demos.",
    },
    {
      title: "Data & Problem Solving",
      description: "DSA, competitive practice, and applied Python projects.",
    },
    {
      title: "Cloud Starter",
      description: "AWS basics, hosting, and hands-on mini cloud labs.",
    },
  ];

  return (
    <div id="courses" className="py-16 scroll-anchor" data-testid="courses-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div data-testid="courses-header">
          <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500">Free Skill Courses</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-3" data-testid="courses-title">
            Learn the tech stack that hiring companies expect.
          </h1>
          <p className="text-sm text-zinc-300 mt-4" data-testid="courses-subtitle">
            Mentored sessions + peer learning. No extra fees for CODESHED residents.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 mt-12" data-testid="courses-content">
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="courses-list-card">
            <div className="flex items-center gap-3" data-testid="courses-list-header">
              <BookOpen className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="courses-list-title">
                Current Course List
              </h3>
            </div>
            <ul className="mt-4 space-y-3 text-sm text-zinc-300" data-testid="courses-list">
              {courses.map((course, index) => (
                <li key={course} className="flex items-center gap-2" data-testid={`courses-list-item-${index}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-yellow-300" data-testid={`courses-list-dot-${index}`} />
                  {course}
                </li>
              ))}
            </ul>
          </div>
          <div
            className="bg-zinc-900 border border-zinc-800 p-6 flex flex-col justify-between"
            data-testid="courses-structure-card"
          >
            <div>
              <div className="flex items-center gap-3" data-testid="courses-structure-header">
                <Laptop className="text-yellow-300" size={26} />
                <h3 className="font-heading text-xl" data-testid="courses-structure-title">
                  Learning Format
                </h3>
              </div>
              <p className="text-sm text-zinc-300 mt-4" data-testid="courses-structure-description">
                Weekly labs, sprint tasks, and guided roadmaps for students preparing for CSE admissions.
              </p>
            </div>
            <div className="mt-6" data-testid="courses-structure-timeline">
              <div className="text-xs uppercase font-mono text-zinc-500">Timing</div>
              <p className="text-sm text-zinc-300 mt-2" data-testid="courses-structure-timing">
                Morning and evening batches so your college or coaching schedule stays balanced.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3 mt-12" data-testid="courses-tracks-grid">
          {tracks.map((track, index) => (
            <div
              key={track.title}
              className="bg-zinc-900 border border-zinc-800 p-6 hover:-translate-y-1 hover:border-yellow-400/50 transition"
              data-testid={`courses-track-card-${index}`}
            >
              <h3 className="font-heading text-xl" data-testid={`courses-track-title-${index}`}>
                {track.title}
              </h3>
              <p className="text-sm text-zinc-300 mt-3" data-testid={`courses-track-description-${index}`}>
                {track.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdmissionsPage = () => {
  const steps = [
    "Choose the right CSE pathway and entrance exams.",
    "Build fundamentals in programming + DSA.",
    "Create a project portfolio for college and internships.",
    "Prepare for interviews with mock sessions and guidance.",
  ];

  const supports = [
    {
      title: "Internship Guidance",
      description: "Project reviews and help in applying to beginner-friendly internships.",
      icon: <Briefcase className="text-yellow-300" size={24} />,
    },
    {
      title: "Job-Ready Skills",
      description: "Resume building, LinkedIn setup, and real-world project planning.",
      icon: <GraduationCap className="text-yellow-300" size={24} />,
    },
  ];

  return (
    <div id="admissions" className="py-16 scroll-anchor" data-testid="admissions-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div data-testid="admissions-header">
          <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500">Beyond The Degree</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-3" data-testid="admissions-title">
            Guidance after Class 12 for confident career choices.
          </h1>
          <p className="text-sm text-zinc-300 mt-4" data-testid="admissions-subtitle">
            We help you plan admissions, skills, and internships so you stay ahead.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 mt-12" data-testid="admissions-roadmap-section">
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="admissions-roadmap-card">
            <div className="flex items-center gap-3" data-testid="admissions-roadmap-header">
              <GraduationCap className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="admissions-roadmap-title">
                Roadmap for CSE Aspirants
              </h3>
            </div>
            <ul className="mt-4 space-y-3 text-sm text-zinc-300" data-testid="admissions-roadmap-list">
              {steps.map((step, index) => (
                <li key={step} className="flex items-start gap-3" data-testid={`admissions-step-${index}`}>
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-yellow-300" data-testid={`admissions-step-dot-${index}`} />
                  {step}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="admissions-guidance-card">
            <div className="flex items-center gap-3" data-testid="admissions-guidance-header">
              <Briefcase className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="admissions-guidance-title">
                Mentorship Support
              </h3>
            </div>
            <p className="text-sm text-zinc-300 mt-4" data-testid="admissions-guidance-description">
              One-on-one sessions with mentors who have worked in tech and understand the admission path.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 mt-6 text-yellow-300 font-mono text-xs uppercase tracking-[0.3em]"
              data-testid="admissions-guidance-cta"
            >
              Speak to a mentor
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 mt-12" data-testid="admissions-support-grid">
          {supports.map((support, index) => (
            <div key={support.title} className="bg-zinc-900 border border-zinc-800 p-6" data-testid={`admissions-support-card-${index}`}>
              <div className="flex items-center gap-3" data-testid={`admissions-support-header-${index}`}>
                {support.icon}
                <h3 className="font-heading text-xl" data-testid={`admissions-support-title-${index}`}>
                  {support.title}
                </h3>
              </div>
              <p className="text-sm text-zinc-300 mt-3" data-testid={`admissions-support-description-${index}`}>
                {support.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContactPage = () => {
  const initialForm = {
    full_name: "",
    phone: "",
    email: "",
    stream: "",
    percentage: "",
    preferred_course: "",
    city: "",
    message: "",
  };
  const [formData, setFormData] = useState(initialForm);
  const [submissionState, setSubmissionState] = useState({
    loading: false,
    success: false,
    error: "",
  });

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmissionState({ loading: true, success: false, error: "" });
    try {
      const response = await fetch(`${API}/admissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error("Submission failed");
      }
      setSubmissionState({ loading: false, success: true, error: "" });
      setFormData(initialForm);
    } catch (error) {
      setSubmissionState({
        loading: false,
        success: false,
        error: "Unable to submit right now. Please try again.",
      });
    }
  };

  return (
    <div id="contact" className="py-16 scroll-anchor" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div data-testid="contact-header">
          <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500">Admissions</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-3" data-testid="contact-title">
            Share your Class 12 details and start your CODESHED admission.
          </h1>
          <p className="text-sm text-zinc-300 mt-4" data-testid="contact-subtitle">
            We will review your information, guide you on the right course, and reach out quickly.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3 mt-12" data-testid="contact-info-grid">
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="contact-whatsapp-card">
            <div className="flex items-center gap-3" data-testid="contact-whatsapp-header">
              <MessageCircle className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="contact-whatsapp-title">
                WhatsApp
              </h3>
            </div>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-yellow-300"
              data-testid="contact-whatsapp-link"
            >
              7319188649
              <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="contact-email-card">
            <div className="flex items-center gap-3" data-testid="contact-email-header">
              <Mail className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="contact-email-title">
                Email
              </h3>
            </div>
            <a
              href="mailto:saycodeshed@gmail.com"
              className="mt-3 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-yellow-300"
              data-testid="contact-email-link"
            >
              saycodeshed@gmail.com
              <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="contact-location-card">
            <div className="flex items-center gap-3" data-testid="contact-location-header">
              <MapPin className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="contact-location-title">
                Location
              </h3>
            </div>
            <p className="mt-3 text-sm text-zinc-300" data-testid="contact-location-text">
              Barasat, North 24 Parganas
            </p>
          </div>
        </div>

        <div className="grid gap-8 md:grid-cols-2 mt-12" data-testid="contact-form-section">
          <form
            onSubmit={handleSubmit}
            className="bg-zinc-900 border border-zinc-800 p-6 space-y-4"
            data-testid="admission-form"
          >
            <div data-testid="admission-form-field-name">
              <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-name-label">
                Full Name
              </label>
              <input
                type="text"
                required
                value={formData.full_name}
                onChange={handleChange("full_name")}
                className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                placeholder="Enter your name"
                data-testid="admission-name-input"
              />
            </div>
            <div data-testid="admission-form-field-phone">
              <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-phone-label">
                Phone / WhatsApp
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange("phone")}
                className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                placeholder="Your number"
                data-testid="admission-phone-input"
              />
            </div>
            <div data-testid="admission-form-field-email">
              <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-email-label">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={handleChange("email")}
                className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                placeholder="you@email.com"
                data-testid="admission-email-input"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2" data-testid="admission-form-academics-grid">
              <div data-testid="admission-form-field-stream">
                <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-stream-label">
                  12th Stream
                </label>
                <select
                  required
                  value={formData.stream}
                  onChange={handleChange("stream")}
                  className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                  data-testid="admission-stream-select"
                >
                  <option value="" data-testid="admission-stream-option-default">Select stream</option>
                  <option value="Science" data-testid="admission-stream-option-science">Science</option>
                  <option value="Commerce" data-testid="admission-stream-option-commerce">Commerce</option>
                  <option value="Arts" data-testid="admission-stream-option-arts">Arts</option>
                  <option value="Other" data-testid="admission-stream-option-other">Other</option>
                </select>
              </div>
              <div data-testid="admission-form-field-percentage">
                <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-percentage-label">
                  12th Percentage
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.percentage}
                  onChange={handleChange("percentage")}
                  className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                  placeholder="e.g. 85"
                  data-testid="admission-percentage-input"
                />
              </div>
            </div>
            <div data-testid="admission-form-field-course">
              <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-course-label">
                Preferred Course
              </label>
              <input
                type="text"
                required
                value={formData.preferred_course}
                onChange={handleChange("preferred_course")}
                className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                placeholder="CSE, Data Science, Web Dev"
                data-testid="admission-course-input"
              />
            </div>
            <div data-testid="admission-form-field-city">
              <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-city-label">
                City
              </label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={handleChange("city")}
                className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                placeholder="Your city"
                data-testid="admission-city-input"
              />
            </div>
            <div data-testid="admission-form-field-message">
              <label className="text-xs uppercase font-mono tracking-[0.3em] text-zinc-500" data-testid="admission-message-label">
                Message (Optional)
              </label>
              <textarea
                rows="4"
                value={formData.message}
                onChange={handleChange("message")}
                className="mt-2 w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                placeholder="Tell us your goals"
                data-testid="admission-message-input"
              />
            </div>
            <button
              type="submit"
              className="w-full px-4 py-3 bg-yellow-400 text-black font-bold rounded-sm hover:bg-yellow-300 transition disabled:opacity-60"
              disabled={submissionState.loading}
              data-testid="admission-form-submit"
            >
              {submissionState.loading ? "Submitting..." : "Submit Admission"}
            </button>
            {submissionState.success && (
              <div className="text-sm text-yellow-200" data-testid="admission-form-success">
                Thanks! Your admission request has been received.
              </div>
            )}
            {submissionState.error && (
              <div className="text-sm text-red-300" data-testid="admission-form-error">
                {submissionState.error}
              </div>
            )}
          </form>
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="contact-side-card">
            <div className="flex items-center gap-3" data-testid="contact-side-header">
              <Briefcase className="text-yellow-300" size={26} />
              <h3 className="font-heading text-xl" data-testid="contact-side-title">
                Admission Support
              </h3>
            </div>
            <p className="text-sm text-zinc-300 mt-4" data-testid="contact-side-description">
              We evaluate your Class 12 profile and recommend the right CSE pathway.
            </p>
            <div className="mt-6 space-y-3" data-testid="contact-side-details">
              <div className="flex items-center gap-2 text-sm text-zinc-300" data-testid="contact-side-location">
                <MapPin size={16} className="text-yellow-300" />
                Barasat, North 24 Parganas
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-300" data-testid="contact-side-email">
                <Mail size={16} className="text-yellow-300" />
                saycodeshed@gmail.com
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-300" data-testid="contact-side-whatsapp">
                <MessageCircle size={16} className="text-yellow-300" />
                7319188649
              </div>
            </div>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-6 px-4 py-3 border border-yellow-400 text-yellow-200 font-semibold rounded-sm hover:border-yellow-300 hover:text-yellow-100 transition"
              data-testid="contact-side-whatsapp-cta"
            >
              WhatsApp Now
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

const AdminPage = ({ authStatus, user, onLogin, onLogout }) => {
  const [admissions, setAdmissions] = useState([]);
  const [isLoadingAdmissions, setIsLoadingAdmissions] = useState(true);

  const fetchAdmissions = async () => {
    try {
      setIsLoadingAdmissions(true);
      const response = await fetch(`${API}/admissions?limit=12`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to load admissions");
      }
      const data = await response.json();
      setAdmissions(data);
    } catch (error) {
      setAdmissions([]);
    } finally {
      setIsLoadingAdmissions(false);
    }
  };

  useEffect(() => {
    if (authStatus === null) {
      setIsLoadingAdmissions(true);
      return;
    }
    if (authStatus === true) {
      fetchAdmissions();
      return;
    }
    setIsLoadingAdmissions(false);
    setAdmissions([]);
  }, [authStatus]);

  return (
    <div className="py-16" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div data-testid="admin-page-header">
          <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500" data-testid="admin-page-overline">
            Admin Console
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl mt-3" data-testid="admin-page-title">
            Admission Requests
          </h1>
          <p className="text-sm text-zinc-300 mt-3" data-testid="admin-page-subtitle">
            Secure view for the CODESHED team. This page is hidden from the public website.
          </p>
        </div>

        <div className="mt-10 bg-zinc-900 border border-zinc-800 p-6" data-testid="admin-section">
          <div className="flex flex-wrap items-center justify-between gap-4" data-testid="admin-section-header">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase font-mono text-zinc-500" data-testid="admin-section-overline">
                Admin Access
              </div>
              <h3 className="font-heading text-xl" data-testid="admin-section-title">
                Recent Admission Requests
              </h3>
            </div>
            {authStatus === true ? (
              <div className="flex items-center gap-3 text-sm text-zinc-400" data-testid="admin-user">
                <span data-testid="admin-user-email">{user?.email}</span>
                <button
                  type="button"
                  onClick={onLogout}
                  className="border border-zinc-700 px-3 py-1 text-xs uppercase tracking-[0.2em] font-mono hover:border-yellow-400 hover:text-yellow-200 transition"
                  data-testid="admin-logout-button"
                >
                  Log out
                </button>
              </div>
            ) : (
              <div className="text-sm text-zinc-400" data-testid="admin-status">
                {authStatus === null ? "Checking access..." : "Admin login required"}
              </div>
            )}
          </div>

          {authStatus === null ? (
            <p className="text-sm text-zinc-400 mt-6" data-testid="admin-loading">
              Checking admin access...
            </p>
          ) : authStatus === false ? (
            <div className="mt-6" data-testid="admin-login">
              <p className="text-sm text-zinc-400" data-testid="admin-login-text">
                Sign in with Google to view admission requests.
              </p>
              <button
                type="button"
                onClick={onLogin}
                className="mt-4 px-4 py-2 bg-yellow-400 text-black font-bold rounded-sm hover:bg-yellow-300 transition"
                data-testid="admin-login-button"
              >
                Admin Google Login
              </button>
            </div>
          ) : isLoadingAdmissions ? (
            <p className="text-sm text-zinc-400 mt-6" data-testid="admin-loading-data">
              Fetching latest submissions...
            </p>
          ) : admissions.length === 0 ? (
            <p className="text-sm text-zinc-400 mt-6" data-testid="admin-empty">
              No admissions yet.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 mt-6" data-testid="admin-list">
              {admissions.map((entry, index) => (
                <div
                  key={entry.id}
                  className="border border-zinc-800 bg-zinc-950/60 p-4"
                  data-testid={`admin-card-${index}`}
                >
                  <div className="flex items-center justify-between" data-testid={`admin-card-header-${index}`}>
                    <div>
                      <div className="font-heading text-lg" data-testid={`admin-name-${index}`}>
                        {entry.full_name}
                      </div>
                      <div className="text-xs text-zinc-400" data-testid={`admin-stream-${index}`}>
                        {entry.stream} • {entry.percentage}%
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500" data-testid={`admin-date-${index}`}>
                      {new Date(entry.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-zinc-300" data-testid={`admin-course-${index}`}>
                    Preferred: {entry.preferred_course}
                  </div>
                  <div className="mt-2 text-sm text-zinc-400" data-testid={`admin-contact-${index}`}>
                    {entry.phone} • {entry.email}
                  </div>
                  <div className="mt-2 text-sm text-zinc-500" data-testid={`admin-city-${index}`}>
                    City: {entry.city}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function App() {
  const [authStatus, setAuthStatus] = useState(null);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState("");

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}${window.location.pathname}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      setAuthStatus(false);
      setUser(null);
    }
  };

  const handleAuthComplete = (userData) => {
    setUser(userData);
    setAuthStatus(true);
    setAuthError("");
  };

  const handleAuthError = (message) => {
    setAuthStatus(false);
    setAuthError(message);
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!response.ok) {
          throw new Error("Not authenticated");
        }
        const userData = await response.json();
        setUser(userData);
        setAuthStatus(true);
      } catch (error) {
        setAuthStatus(false);
      }
    };
    checkAuth();
  }, []);

  if (window.location.hash?.includes("session_id=")) {
    return <AuthCallback onComplete={handleAuthComplete} onError={handleAuthError} />;
  }

  return (
    <BrowserRouter>
      <Layout>
        {authError ? (
          <div
            className="max-w-7xl mx-auto px-6 md:px-8 mt-6 text-sm text-red-300"
            data-testid="auth-error-banner"
          >
            {authError}
          </div>
        ) : null}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/admin"
            element={<AdminPage authStatus={authStatus} user={user} onLogin={handleLogin} onLogout={handleLogout} />}
          />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
